"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, SunMedium, Briefcase, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Seasonality & Wedding Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage high-compression wedding dates and winter leisure spikes against intense summer dips. We establish dynamic minimum length-of-stay rules and automated rate yields across Gomti Nagar and Hazratganj comp-sets.",
  metricLabel: "Strategy focus",
  metricValue: "Compression yield fences & minimum stay controls"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate movements across MakeMyTrip, Agoda, and Booking.com. Automated market alerts and real-time adjustments protect your RevPAR while securing high-intent bookings ahead of market discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA tracking & rule-based rate hedging"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim margins lost to steep OTA commissions. We enforce strict rate parity across metasearch engines and drive direct corporate and banquet guest conversions.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct booking growth"
}];
const seasonalityPhases = [{
  title: "Peak Season (Oct – Mar)",
  badge: "Maximum Yield",
  icon: Sparkles,
  description: "High wedding dates, government conventions, medical congresses, and heritage tourists drive peak RevPAR. Focus on unconstrained demand pricing, strict non-refundable fences, and multi-night minimum stays.",
  kpi: "Primary KPI: ADR & TrevPAR Maximization"
}, {
  title: "Corporate Flow (Year-Round)",
  badge: "Midweek Anchor",
  icon: Briefcase,
  description: "Steady commercial travel across Gomti Nagar, Vibhuti Khand, and Transport Nagar. Maintain contracted corporate tiers, negotiated GDS rates, and dynamic weekend leisure packages to balance occupancies.",
  kpi: "Primary KPI: Base Occupancy & Direct RFP Retention"
}, {
  title: "Summer & Shoulder (Apr – Jul)",
  badge: "Volume Balancing",
  icon: SunMedium,
  description: "High temperatures slow leisure walk-ins while transit and examination traffic rises. Deploy targeted geo-rates, value-added inclusions, and flash OTA promotions to protect RevPAR without margin dilution.",
  kpi: "Primary KPI: Channel Mix & Net Yield Protection"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapelucknow" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Lucknow
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Lucknow presents a fast-growing micro-market powered by commercial corporate travel, large-scale destination weddings, state government summits, and heritage cultural tourism. Sunil Singh designs high-precision revenue strategies built specifically for Lucknow's luxury hotels, boutique stays, and banqueting properties.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <div className="mt-16 md:mt-20 rounded-xl border border-border bg-card p-6 md:p-10 shadow-sm">
          <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary">
                Demand Cycles & Market Timing
              </span>
              <h3 className="font-serif text-2xl md:text-3xl font-bold mt-2 text-foreground">
                Lucknow Hospitality Seasonality Overview
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Data-backed pacing models adapt room rates and minimum stay policies to capitalize on city-wide demand shifts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityPhases.map((phase, idx) => {
            const Icon = phase.icon;
            return <div key={phase.title} data-index={idx} className="rounded-lg border border-border/80 bg-background/60 p-5 flex flex-col justify-between space-y-4">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                        <Icon className="size-4 text-chart-2" />
                      </div>
                      <Badge variant="secondary" className="text-[11px] font-medium">
                        {phase.badge}
                      </Badge>
                    </div>
                    <h4 className="font-serif text-lg font-semibold text-foreground">
                      {phase.title}
                    </h4>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                      {phase.description}
                    </p>
                  </div>
                  <div className="pt-3 border-t border-border/60">
                    <span className="text-[11px] font-semibold text-secondary">
                      {phase.kpi}
                    </span>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Lucknow hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}